import type { AIPersonaOut } from '@repo/api-codegen';
import { useGetPersonas } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import type { MenuItem } from '@repo/secoda-editor';
import { capitalize } from 'lodash-es';
import type { ISecodaEntity, SearchResult } from '../../../../../api';
import { useMention, useUserList } from '../../../../../api';
import { buildResourceUrl } from '../../../../../utils/navigationUtils';

export interface MentionMenuItem extends MenuItem {
	description?: string;
	entity?: ISecodaEntity;
	persona?: AIPersonaOut;
	isDefaultAiPersona?: boolean;
	attrs?: {
		markup: string;
		'data-name': string;
		href: string;
		title: string;

		// to_entity is null for users.
		to_entity?: string;
	};
}

interface IUseMentionMenuArgs {
	searchTerm?: string;
	includeAiPersonas?: boolean;
}

interface IUseMentionMenuResponse {
	isLoading: boolean;
	data: MentionMenuItem[];
}

const SECODA_AI = {
	name: 'resource_link',
	title: 'Secoda AI',
	description: 'Default persona',
	isDefaultAiPersona: true,
	attrs: {
		markup: 'Secoda AI',
		'data-name': 'Secoda AI',
		title: 'Secoda AI',
		href: `/ai/new`,
	},
} as MentionMenuItem;

// Optimizes mentions by always showing fetched users first before doing a full search
// Fetched users are usually cached through reactQuery from other parts of the app so it's usually instant.
export function useMentionMenu({
	searchTerm = '',
	includeAiPersonas = false,
}: IUseMentionMenuArgs): IUseMentionMenuResponse {
	const { data: personas, isLoading: isPersonasLoading } = useGetPersonas(
		{},
		{
			enabled: includeAiPersonas,
			select: (data) =>
				[
					SECODA_AI,
					...data.map(
						(persona) =>
							({
								name: 'resource_link',
								title: persona.name ? persona.name : 'New persona',
								description: persona.description ?? 'AI persona',
								persona,
								attrs: {
									markup: persona.name ? persona.name : 'New persona',
									'data-name': persona.name ? persona.name : 'New persona',
									title: persona.name ? persona.name : 'New persona',
									href: `/ai/new?persona=${persona.id}`,
								},
							}) as MentionMenuItem
					),
				].filter((persona) =>
					persona.title?.toLowerCase()?.includes(searchTerm.toLowerCase())
				),
		}
	);

	const { data: users, isLoading: isUserLoading } = useUserList({
		filters: {
			is_service_account: false,
			disabled: false,
		},
		options: {
			select: (data) =>
				data.results
					.filter(
						(user) =>
							user.display_name
								.toLowerCase()
								.includes(searchTerm.toLowerCase()) ||
							user.email.toLowerCase().includes(searchTerm.toLowerCase())
					)
					.map(
						(user) =>
							({
								name: 'resource_link',
								title: user.display_name,
								description: 'User',
								entity: {
									id: user.id,
									title: user.display_name,
									entity_type: EntityType.user,
									published: true,
									workspace_id: user.workspace_id,
								} as ISecodaEntity,
								attrs: {
									markup: user.display_name,
									'data-name': user.display_name,
									title: user.display_name,
									href: `/user/${user.id}`,
								},
							}) as MentionMenuItem
					)
					.slice(0, 3),
		},
	});

	const { isFetching: isSearchLoading, data: searchResults } = useMention({
		searchTerm,
		options: {
			select: (data) =>
				data.results.map((r: SearchResult) => {
					let displayedTitle = r.title;

					if (r.entity_type === EntityType.table) {
						displayedTitle = `${r.search_metadata?.schema}.${r.title}`;
					} else if (r.entity_type === EntityType.column) {
						displayedTitle = `${r.search_metadata?.schema}.${r.search_metadata?.table}.${r.title}`;
					}

					return {
						name: 'resource_link',
						title: displayedTitle,
						description: capitalize(r.entity_type?.replaceAll('_', ' ')),
						entity: {
							id: r.id,
							icon: r.icon,
							title: r.title,
							entity_type: r.entity_type,
							published: true,
							workspace_id: r.workspace,
							integration: r.integration,
							search_metadata: r.search_metadata,
						} as ISecodaEntity,
						attrs: {
							markup: r.title,
							'data-name': r.title,
							title: r.title,
							href: buildResourceUrl(r),
							to_entity: r.id,
						},
					} as MentionMenuItem;
				}),
		},
	});

	const userSeparator: MentionMenuItem[] =
		users?.length || (includeAiPersonas && personas?.length)
			? [
					{
						name: 'separator',
						title: 'Users',
					} as MentionMenuItem,
				]
			: [];

	const searchSeparator: MentionMenuItem[] =
		searchResults?.length || isSearchLoading
			? [
					{
						name: 'separator',
						title: 'Resources',
					} as MentionMenuItem,
				]
			: [];

	return {
		isLoading: isUserLoading || isSearchLoading || isPersonasLoading,
		data: [
			...userSeparator,
			...(includeAiPersonas ? (personas ?? []) : []),
			...(users ?? []),
			...searchSeparator,
			...(searchResults ?? []),
		],
	};
}
